import { Box, useTheme } from '@mui/material'
import * as React from 'react'
import { useEffect, useState } from 'react'
import {
    HomeIcon,
    ListAltIcon,
    SeriesIcon,
    TvIcon,
    VodIcon,
} from '@/shared/ui/Icons'
import { TopMenuButton } from '@/shared/ui/TopMenuButton'
import { useTopMenu } from '@/widgets/TopMenu/hooks/useTopMenu'
import { tvPagePath } from '@/shared/constants/tvConstants'
import { epgPagePath } from '@/shared/constants/epgConstants'
import { vodPagePath } from '@/shared/constants/vodConstants'
import { homePagePath } from '@/shared/constants/homeConstants'
import {
    useAppStore,
    useTVStore,
    useVisibilityStore,
    useVODStore,
} from '@/shared/store'
import {
    epgIconStyle,
    homeIconStyle,
    tvIconStyle,
    vodIconStyle,
} from './menuButtonsIconsStyles'
import { useLocation } from 'react-router-dom'
import { seriesPagePath } from '@/shared/constants/seriesConstants'
import { isStreamIQ } from '@/shared/lib'
import {
    FocusContext,
    setFocus,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import {
    DIRECTION_UP_FOCUS_KEY,
    MENU_BUTTONS_FOCUS_KEY,
} from '@/shared/constants/focusLibraryConstants'

const MenuButtons = () => {
    const theme = useTheme()
    const {
        isTvContentVisible,
        isVodContentVisible,
        isSeriesContentVisible,
        isEpgContentVisible,
        isHomeContentVisible,
    } = useVisibilityStore()
    const { isUserLoggedIn } = useAppStore()
    const { setIsVodDetailsPageRendered } = useVODStore()
    const {
        handleTvClick,
        handleEpgClick,
        handleVodClick,
        handleHomeClick,
        handleSeriesClick,
    } = useTopMenu()
    const { pathname } = useLocation()
    const [isTVActive, setIsTVActive] = useState(false)
    const { isPIPMode } = useTVStore()

    useEffect(() => {
        if (isTvContentVisible && isPIPMode && pathname !== tvPagePath) {
            setIsTVActive(false)
        } else if (isTvContentVisible && pathname !== tvPagePath) {
            setIsTVActive(false)
        } else if (pathname !== tvPagePath) {
            setIsTVActive(false)
        } else {
            setIsTVActive(true)
        }
    }, [isTvContentVisible, isPIPMode, pathname])

    const { ref: menuRef } = useFocusable({
        focusKey: MENU_BUTTONS_FOCUS_KEY,

        focusable: true,
        isFocusBoundary: true,
        focusBoundaryDirections: [DIRECTION_UP_FOCUS_KEY],

        onFocus: () => {
            menuRef.current.focus()
        },
    })

    useEffect(() => {
        setFocus(homePagePath)
    }, [isUserLoggedIn])

    return (
        <FocusContext.Provider value={MENU_BUTTONS_FOCUS_KEY}>
            <Box
                ref={menuRef}
                display="flex"
                gap={{ xs: '20px', md: '30px' }}
                mr={!isUserLoggedIn ? '140px' : 0}>
                <TopMenuButton
                    dataTestId="home-button"
                    onClickFunction={handleHomeClick}
                    buttonText="HOME"
                    buttonIcon={HomeIcon(
                        isHomeContentVisible ? theme.palette.primary.main : ''
                    )}
                    active={isHomeContentVisible}
                    destination={homePagePath}
                    isDisabled={!isUserLoggedIn}
                    customIconStyle={homeIconStyle}
                    focusKey={homePagePath}
                />
                <TopMenuButton
                    dataTestId="tv-button"
                    onClickFunction={handleTvClick}
                    buttonText="TV"
                    buttonIcon={TvIcon(
                        isTVActive ? theme.palette.primary.main : ''
                    )}
                    active={isTVActive}
                    destination={tvPagePath}
                    isDisabled={!isUserLoggedIn}
                    customIconStyle={tvIconStyle}
                    focusKey={tvPagePath}
                />
                <TopMenuButton
                    dataTestId="epg-button"
                    onClickFunction={handleEpgClick}
                    buttonText="EPG"
                    buttonIcon={ListAltIcon(
                        isEpgContentVisible ? theme.palette.primary.main : ''
                    )}
                    active={isEpgContentVisible}
                    destination={epgPagePath}
                    isDisabled={!isUserLoggedIn}
                    customIconStyle={epgIconStyle}
                    focusKey={epgPagePath}
                />

                {isStreamIQ() && (
                    <TopMenuButton
                        dataTestId="series-button"
                        onClickFunction={() => {
                            handleSeriesClick(true)
                            setIsVodDetailsPageRendered(false)
                        }}
                        buttonText="SERIES"
                        buttonIcon={SeriesIcon(
                            isSeriesContentVisible
                                ? theme.palette.primary.main
                                : ''
                        )}
                        active={isSeriesContentVisible}
                        destination={seriesPagePath}
                        isDisabled={!isUserLoggedIn}
                        customIconStyle={epgIconStyle}
                        focusKey={seriesPagePath}
                    />
                )}

                <TopMenuButton
                    dataTestId="vod-button"
                    onClickFunction={() => {
                        handleVodClick(true)
                        setIsVodDetailsPageRendered(false)
                    }}
                    buttonText="VOD"
                    buttonIcon={VodIcon(
                        isVodContentVisible ? theme.palette.primary.main : ''
                    )}
                    active={isVodContentVisible}
                    destination={vodPagePath}
                    isDisabled={!isUserLoggedIn}
                    customIconStyle={vodIconStyle}
                    focusKey={vodPagePath}
                />
            </Box>
        </FocusContext.Provider>
    )
}

export { MenuButtons }
